import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Target, Check } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function WaitlistForm({
  source = 'landing_page',
  title = 'Join Early Access',
  subtitle = 'Be among the first traders to access Trading Pilot 360.',
  subtext = 'No payment required. Early users will receive priority access to new features.',
  buttonLabel = 'Join Early Access (Free)',
  onJoined,
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [joined, setJoined] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }
    if (!consent) {
      toast.error('Please agree to the Terms of Service and Privacy Policy');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name: name || null, source, consent_given: true }),
      });
      const data = await res.json();
      if (data.status === 'success' || data.status === 'exists') {
        setJoined(true);
        toast.success(data.message);
        if (window.gtag) {
          window.gtag('event', 'waitlist_signup', {
            event_category: 'conversion',
            event_label: source,
          });
        }
        if (onJoined) onJoined();
      } else {
        toast.error(data.message || data.detail || 'Something went wrong');
      }
    } catch (err) {
      toast.error('Failed to join waitlist');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="bg-[#13131a] border-white/5 p-8" data-testid={`waitlist-form-${source}`}>
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-blue-500/10 rounded-full flex items-center justify-center text-blue-500 mx-auto mb-4">
          <Target className="w-8 h-8" />
        </div>
        <h2 className="text-2xl font-bold mb-2">{title}</h2>
        <p className="text-gray-400">{subtitle}</p>
        {subtext && (
          <p className="text-gray-500 text-sm mt-2">{subtext}</p>
        )}
      </div>

      {joined ? (
        <div className="text-center py-8" data-testid={`waitlist-joined-${source}`}>
          <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center text-green-500 mx-auto mb-4">
            <Check className="w-8 h-8" />
          </div>
          <h3 className="text-xl font-semibold mb-2">You're on the early access list!</h3>
          <p className="text-gray-400">We'll notify you when your access is ready.</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            type="email"
            placeholder="Your email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="bg-[#0a0a0c] border-white/10 h-12"
            data-testid={`waitlist-email-${source}`}
            required
          />
          <Input
            type="text"
            placeholder="Your name (optional)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="bg-[#0a0a0c] border-white/10 h-12"
            data-testid={`waitlist-name-${source}`}
          />
          <label
            className="flex items-start space-x-2 text-xs text-gray-400 cursor-pointer select-none"
            data-testid={`waitlist-consent-label-${source}`}
          >
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-0.5 w-4 h-4 flex-shrink-0 rounded border-white/20 bg-[#0a0a0c] accent-blue-600 cursor-pointer"
              data-testid={`waitlist-consent-${source}`}
              required
            />
            <span className="leading-relaxed">
              I agree to the{' '}
              <a
                href="/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300 underline"
                data-testid={`waitlist-terms-link-${source}`}
              >
                Terms of Service
              </a>{' '}
              and{' '}
              <a
                href="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300 underline"
                data-testid={`waitlist-privacy-link-${source}`}
              >
                Privacy Policy
              </a>
              .
            </span>
          </label>
          <Button
            type="submit"
            disabled={loading || !consent}
            className="w-full bg-blue-600 hover:bg-blue-700 h-12 text-lg font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid={`waitlist-submit-${source}`}
          >
            {loading ? 'Joining...' : buttonLabel}
          </Button>
          <p className="text-xs text-gray-500 text-center">
            No spam, ever. We'll only email you about access and important updates.
          </p>
        </form>
      )}
    </Card>
  );
}
